import { api } from './api';
import { useAuthStore } from '../stores/authStore';

function decodePublicKey(value: unknown): Uint8Array {
  const data = typeof value === 'string' ? Uint8Array.from(atob(value), char => char.charCodeAt(0)) :
    value && typeof value === 'object' && 'data' in value && Array.isArray(value.data) ? Uint8Array.from(value.data) : new Uint8Array();
  if (data.length !== 32) throw new Error('Invalid public key');
  return data;
}

/**
 * Decrypt the user's X25519 encryption private key from the auth store.
 *
 * The auth store holds:
 * - masterKey (Uint8Array) — derived from password via Argon2id
 * - encryptedPrivateKeys (base64) — Ed25519+X25519 private keys encrypted with KEK
 * - privateKeyNonce (base64) — nonce for decryption
 * - kekSalt (base64) — salt for KEK derivation
 *
 * Returns the 32-byte X25519 private key, or null if vault is locked.
 */
async function getEncryptionPrivateKey(): Promise<Uint8Array | null> {
  const state = useAuthStore.getState();
  const { masterKey, encryptedPrivateKeys, privateKeyNonce, kekSalt } = state;

  if (!masterKey || !encryptedPrivateKeys || !privateKeyNonce || !kekSalt) {
    return null;
  }

  const { deriveKEKWithSalt, decrypt, initializeCrypto, zeroMemory } = await import('@barmetvault/crypto');
  await initializeCrypto();

  const decode = (value: string) =>
    Uint8Array.from(atob(value), (char) => char.charCodeAt(0));

  const privateKek = await deriveKEKWithSalt(masterKey, 'private-keys', decode(kekSalt));
  let privateKeysBlob: Uint8Array | undefined;
  try {
    privateKeysBlob = decrypt(decode(encryptedPrivateKeys), decode(privateKeyNonce), privateKek);
    // Registration stores Ed25519 secret (64 bytes) followed by X25519 secret (32 bytes).
    if (privateKeysBlob.length !== 96) throw new Error('Unsupported private key envelope');
    if (useAuthStore.getState().masterKey !== masterKey) throw new Error('Secrets locked');
    return privateKeysBlob.slice(64, 96);
  } finally { zeroMemory(privateKek); if (privateKeysBlob) zeroMemory(privateKeysBlob); }

}

/**
 * Wrap an item's DEK for a recipient using ECDH + HKDF + XChaCha20.
 *
 * Flow:
 * 1. Get recipient's X25519 public key from API
 * 2. Perform ECDH with our private key + recipient's public key
 * 3. Derive wrapping key via HKDF
 * 4. Encrypt (wrap) the DEK with the wrapping key
 * 5. Return base64-encoded wrapped DEK + nonce
 */
export async function wrapDekForRecipient(
  itemDek: Uint8Array,
  recipientUserId: string,
): Promise<{ itemKeyEncForRecipient: string; wrappedKeyNonce: string }> {
  // Dynamic import to avoid loading crypto bundle unless needed
  const { ecdhAgreement, deriveSharedKey } = await import('@barmetvault/crypto');
  const { encrypt, zeroMemory } = await import('@barmetvault/crypto');

  // 1. Get recipient's public key
  const { data: recipient } = await api.get(`/users/${recipientUserId}`);
  const recipientPubKey = decodePublicKey(recipient.publicKeyX25519);

  // 2. Get our X25519 private key (decrypted from auth store)
  const encPrivKey = await getEncryptionPrivateKey();
  if (!encPrivKey) {
    throw new Error('No private key available — unlock your vault first');
  }

  let sharedSecret: Uint8Array | undefined;
  let wrappingKey: Uint8Array | undefined;
  try {
    sharedSecret = ecdhAgreement(encPrivKey, recipientPubKey);
    wrappingKey = await deriveSharedKey(sharedSecret, 'item-dek-share');
    const { ciphertext, nonce } = encrypt(itemDek, wrappingKey);
    return { itemKeyEncForRecipient: btoa(String.fromCharCode(...ciphertext)), wrappedKeyNonce: btoa(String.fromCharCode(...nonce)) };
  } finally { zeroMemory(encPrivKey); if (sharedSecret) zeroMemory(sharedSecret); if (wrappingKey) zeroMemory(wrappingKey); }

}

/**
 * Unwrap a shared item's DEK using ECDH + HKDF + XChaCha20.
 *
 * Flow:
 * 1. Get grantor's X25519 public key from API
 * 2. Perform ECDH with our private key + grantor's public key
 * 3. Derive wrapping key via HKDF
 * 4. Decrypt (unwrap) the DEK
 * 5. Return the raw DEK
 */
export async function unwrapDekFromGrantor(
  wrappedDekBase64: string,
  wrappedNonceBase64: string,
  grantorUserId: string,
): Promise<Uint8Array> {
  const { ecdhAgreement, deriveSharedKey } = await import('@barmetvault/crypto');
  const { decrypt, zeroMemory } = await import('@barmetvault/crypto');

  // 1. Get grantor's public key
  const { data: grantor } = await api.get(`/users/${grantorUserId}`);
  const grantorPubKey = decodePublicKey(grantor.publicKeyX25519);

  // 2. Get our X25519 private key (decrypted from auth store)
  const encPrivKey = await getEncryptionPrivateKey();
  if (!encPrivKey) {
    throw new Error('No private key available — unlock your vault first');
  }

  let sharedSecret: Uint8Array | undefined;
  let wrappingKey: Uint8Array | undefined;
  try {
    sharedSecret = ecdhAgreement(encPrivKey, grantorPubKey);
    wrappingKey = await deriveSharedKey(sharedSecret, 'item-dek-share');
    const decode = (value: string) => Uint8Array.from(atob(value), char => char.charCodeAt(0));
    return decrypt(decode(wrappedDekBase64), decode(wrappedNonceBase64), wrappingKey);
  } finally { zeroMemory(encPrivKey); if (sharedSecret) zeroMemory(sharedSecret); if (wrappingKey) zeroMemory(wrappingKey); }

}

/**
 * Search users by email (for sharing).
 */
export async function searchUsers(query: string) {
  const { data } = await api.get('/users/search', { params: { q: query } });
  return data;
}

/**
 * Invite a user by email to receive a shared item.
 * If the user exists in the org, returns { found: true, userId }.
 * If not, returns { found: false, invited: true, email }.
 */
export async function inviteByEmail(itemId: string, email: string, permissions?: Record<string, boolean>) {
  const { data } = await api.post('/shares/invite-email', { itemId, email, permissions });
  return data;
}

/**
 * Get a user by ID (for unwrapping shared items).
 */
export async function getUserById(userId: string) {
  const { data } = await api.get(`/users/${userId}`);
  return data;
}
